import { Controller } from '@hotwired/stimulus'
import { format as formatTz, utcToZonedTime } from 'date-fns-tz'
import parseISO from 'date-fns/parseISO'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import escapeHtml from '../../utils/sanitize'
import '../../../css/associated-person/associated-person.scss'
import {
  trans,
  ALIAS_PREFIX,
  LOADING,
  STATUS_BANNED,
  STATUS_UNVERIFIED,
  DATE_OF_BIRTH_SHORT,
  DATE_OF_BIRTH_UNKNOWN_SHORT,
  AGE_SHORT,
  AGE_RANGE,
  INCIDENT_COUNT,
  LIST_NO_RESULTS,
  LOADING_FAILED,
  RECORD_DELETED_TOAST
} from '../../translator'
import { getTimestampFormat } from '../../utils/utils'

declare const accountDateFormat: string
declare const accountTimezone: string
declare const accountIsUseAmPm: string
declare const selectedTimezone: string

interface ApIndicators {
  is_banned: boolean
  prior_incidents_count: number
  has_photo: boolean
}

interface ApPhoto {
  is_primary: boolean
  thumbnail_url: string
}

 
interface ApTier3Fields {}

interface ApCriminalFlag {
  type: string
  label: string
  severity: string
}

interface ApAgeRange {
  from: number
  to: number
}

interface ApResult {
  id: string
  full_name: string
  date_of_birth: string | null
  date_of_birth_display: string | null
  age_range: ApAgeRange | null
  is_unverified: boolean
  indicators: ApIndicators
  criminal_history_flags: ApCriminalFlag[]
  status?: string
  aliases?: string[]
  photos?: ApPhoto[]
  updated_at?: string | null
  created_at?: string
  tier: number
  tier_3_fields: ApTier3Fields | null
}

interface ApiResponse {
  results: ApResult[]
  total_result_count: number
  pagination?: {
    page: number
    per_page: number
  }
}

export default class IndexAssociatedPersonsController extends Controller {
  static targets = ['query', 'results', 'filterChip', 'pagination']

  declare readonly queryTarget: HTMLInputElement
  declare readonly resultsTarget: HTMLElement
  declare readonly filterChipTargets: HTMLElement[]
  declare readonly paginationTarget: HTMLElement
  declare readonly hasPaginationTarget: boolean

  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private activeFilterKey = 'all'
  private currentPage = 1
  private perPage = 25
  private totalResultCount = 0

  connect (): void {
    this.search = this.search.bind(this)
    this.filter = this.filter.bind(this)
    this.paginate = this.paginate.bind(this)
    this.syncFilterChips()
    this.runSearch(1)
    this.notifyIfDeleted()
  }

  /** Surfaces the "Record deleted" toast after a redirect from the Show screen, then cleans the URL. */
  private notifyIfDeleted (): void {
    const params = new URLSearchParams(window.location.search)
    if (params.get('deleted') !== '1') {
      return
    }

    const jquery = (window as unknown as { $?: { notify?: (opts: object, settings: object) => void } }).$
    if (jquery !== undefined && typeof jquery.notify === 'function') {
      jquery.notify(
        { message: trans(RECORD_DELETED_TOAST, {}, 'associated-persons') },
        { type: 'success', placement: { from: 'bottom', align: 'right' } }
      )
    }

    params.delete('deleted')
    const query = params.toString()
    window.history.replaceState({}, '', window.location.pathname + (query === '' ? '' : `?${query}`))
  }

  filter (event: Event): void {
    const chip = event.currentTarget

    if (!(chip instanceof HTMLElement)) {
      return
    }

    this.activeFilterKey = chip.dataset.filterKey ?? 'all'
    this.syncFilterChips()
    this.runSearch(1)
  }

  paginate (event: { detail?: { page?: number } }): void {
    const requestedPage = Number(event.detail?.page)

    if (!Number.isInteger(requestedPage) || requestedPage < 1 || requestedPage === this.currentPage) {
      return
    }

    this.runSearch(requestedPage)
  }

  keydown (): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }

  keyup (): void {
    this.debounceTimer = setTimeout(() => {
      this.runSearch(1)
    }, 300)
  }

  private runSearch (page: number): void {
    this.search(page).catch(() => undefined)
  }

  private async search (page = this.currentPage): Promise<void> {
    const query = this.queryTarget.value.trim()

    if (query.length < 2 && query.length !== 0) {
      return
    }

    this.setLoading(true)

    try {
      const url = this.buildUrl(query, page)
      const response = await fetchWithHandlingLoginRedirect(
        url
      )
      const data = await response.json() as ApiResponse

      this.currentPage = data.pagination?.page ?? page
      this.perPage = data.pagination?.per_page ?? this.perPage
      this.totalResultCount = data.total_result_count
      this.renderPagination()

      if (data.total_result_count === 0) {
        this.resultsTarget.innerHTML = this.renderEmpty()
        return
      }

      this.resultsTarget.innerHTML = data.results.map(ap => this.renderCard(ap)).join('')
    } catch {
      this.resultsTarget.innerHTML = this.renderError()
    } finally {
      this.setLoading(false)
    }
  }

  private setLoading (loading: boolean): void {
    if (loading) {
      this.resultsTarget.innerHTML = this.renderLoading()
    }
  }

  private buildUrl (query: string, page: number): string {
    const params = new URLSearchParams()

    if (query.length > 0) {
      params.set('query', query)
    }

    switch (this.activeFilterKey) {
      case 'banned':
        params.set('is_banned', 'true')
        break
      case 'unverified':
        params.set('is_unverified', 'true')
        break
      case 'linkedIssues':
        params.set('has_linked_issues', 'true')
        break
      case 'all':
      default:
        break
    }

    if (page !== 1 || this.perPage !== 25) {
      params.set('page', String(page))
      params.set('per_page', String(this.perPage))
    }

    const queryString = params.toString()

    return queryString === ''
      ? '/internal/api/associated-persons'
      : `/internal/api/associated-persons?${queryString}`
  }

  private syncFilterChips (): void {
    this.filterChipTargets.forEach(chip => {
      if (chip.dataset.filterKey === this.activeFilterKey) {
        chip.classList.add('btn-raven-reg')
        chip.classList.remove('btn-raven-clear')
      } else {
        chip.classList.remove('btn-raven-reg')
        chip.classList.add('btn-raven-clear')
      }
    })
  }

  private renderLoading (): string {
    return `
      <div class="associated-persons-state py-5 text-center text-muted" role="status" aria-live="polite">
        <div class="spinner-border spinner-border-sm mr-2" aria-hidden="true"></div>
        <span>${trans(LOADING, {}, 'organisation')}</span>
      </div>
    `
  }

  private renderCard (ap: ApResult): string {
    const recentDate = this.renderRecentDate(ap)

    return `
      <a href="/associated-persons/${escapeHtml(ap.id)}" class="associated-person-row raven-border-light" data-tier="${escapeHtml(String(ap.tier))}">
        <div class="associated-person-row__main">
          ${this.renderAvatar(ap)}
          <div class="associated-person-row__content">
            <span class="associated-person-row__name">${escapeHtml(ap.full_name)}</span>
            <span class="associated-person-row__meta">${this.renderIdentitySummary(ap)}</span>
          </div>
        </div>
        <div class="associated-person-row__aside">
          <div class="associated-person-row__chips">${this.renderBadges(ap)}${this.renderIncidentCount(ap.indicators.prior_incidents_count)}</div>
          ${recentDate === '' ? '' : `<div class="associated-person-row__date">${recentDate}</div>`}
        </div>
      </a>
    `
  }

  private renderAvatar (ap: ApResult): string {
    const photos = ap.photos ?? []
    const primary = photos.find(photo => photo.is_primary) ?? photos[0]

    if (primary !== undefined) {
      return `<div class="associated-person-row__avatar associated-person-row__avatar--photo" aria-hidden="true"><img src="${escapeHtml(primary.thumbnail_url)}" alt=""></div>`
    }

    const avatar = this.avatarColours(ap.full_name)

    return `<div class="associated-person-row__avatar" style="background-color:${avatar.bg};color:${avatar.fg}" aria-hidden="true">${this.getInitials(ap.full_name)}</div>`
  }

  private renderIdentitySummary (ap: ApResult): string {
    const parts: string[] = []
    const aliases = (ap.aliases ?? []).filter((a): a is string => typeof a === 'string' && a.trim() !== '')

    if (aliases.length > 0) {
      parts.push(`${trans(ALIAS_PREFIX, {}, 'associated-persons')} ${aliases.map(a => escapeHtml(a.trim())).join(', ')}`)
    }

    parts.push(this.renderDob(ap.date_of_birth, ap.date_of_birth_display, ap.age_range))

    return parts.join(' · ')
  }

  private renderDob (dob: string | null, dobDisplay: string | null, ageRange: ApAgeRange | null): string {
    let dobText: string

    if (dob !== null) {
      const dateFormat = accountDateFormat.replace('mm', 'MM')
      dobText = `${trans(DATE_OF_BIRTH_SHORT, {}, 'associated-persons')} ${formatTz(parseISO(dob), dateFormat)}`
    } else if (dobDisplay !== null) {
      dobText = `${trans(DATE_OF_BIRTH_SHORT, {}, 'associated-persons')} ${escapeHtml(dobDisplay)}`
    } else {
      dobText = trans(DATE_OF_BIRTH_UNKNOWN_SHORT, {}, 'associated-persons')
    }

    return dobText + this.renderAgeRange(ageRange)
  }

  private renderAgeRange (ageRange: ApAgeRange | null): string {
    if (ageRange === null) {
      return ''
    }

    const age = ageRange.from === ageRange.to
      ? String(ageRange.from)
      : trans(AGE_RANGE, { '%from%': ageRange.from, '%to%': ageRange.to }, 'associated-persons')

    return ` · ${trans(AGE_SHORT, {}, 'associated-persons')} ${age}`
  }

  private renderBadges (ap: ApResult): string {
    const badges: string[] = []
    if (ap.indicators.is_banned) {
      badges.push(`<span class="associated-person-chip associated-person-chip--danger">${trans(STATUS_BANNED, {}, 'associated-persons')}</span>`)
    }
    // The transformer already orders the flags (severity-descending) and supplies the translated label.
    for (const flag of ap.criminal_history_flags ?? []) {
      badges.push(`<span class="associated-person-chip associated-person-chip--${escapeHtml(flag.severity)}">${escapeHtml(flag.label)}</span>`)
    }
    if (ap.is_unverified) {
      badges.push(`<span class="associated-person-chip associated-person-chip--warning">${trans(STATUS_UNVERIFIED, {}, 'associated-persons')}</span>`)
    }
    return badges.join('')
  }

  private renderIncidentCount (count: number): string {
    if (count === 0) return ''
    return `<span class="associated-person-chip associated-person-chip--neutral">${trans(INCIDENT_COUNT, { '%count%': count }, 'associated-persons')}</span>`
  }

  private renderEmpty (): string {
    return `<div class="associated-persons-state py-5 text-center text-muted">${trans(LIST_NO_RESULTS, {}, 'associated-persons')}</div>`
  }

  private renderPagination (): void {
    if (!this.hasPaginationTarget) {
      return
    }

    const totalPages = Math.ceil(this.totalResultCount / this.perPage)

    if (totalPages <= 1) {
      this.paginationTarget.innerHTML = ''
      return
    }

    const rangeStart = (this.currentPage - 1) * this.perPage + 1
    const rangeEnd = Math.min(this.currentPage * this.perPage, this.totalResultCount)

    this.paginationTarget.innerHTML = `
      <nav aria-label="Associated person result pages">
        <div class="row">
          <div class="col"></div>
          <div class="col mt-1">
            <ul class="pagination justify-content-end mb-0">
              <small class="text-muted vertical-center mr-4">${rangeStart}-${rangeEnd} / ${this.totalResultCount}</small>
              ${this.currentPage > 1 ? this.renderPaginationControl(1, 'fas fa-angle-double-left', 'First page') : ''}
              ${this.currentPage > 1 ? this.renderPaginationControl(this.currentPage - 1, 'fas fa-angle-left', 'Previous page') : ''}
              <li class="page-item active"><div class="page-link">${this.currentPage}</div></li>
              ${this.currentPage < totalPages ? this.renderPaginationControl(this.currentPage + 1, 'fas fa-angle-right', 'Next page') : ''}
            </ul>
          </div>
        </div>
      </nav>
    `
  }

  private renderPaginationControl (page: number, iconClass: string, label: string): string {
    return `
      <li class="page-item" data-action="click->pagination#goToPage" data-pagination-page-param="${page}">
        <div class="page-link">
          <i class="${iconClass}"></i>
          <span class="sr-only">${escapeHtml(label)}</span>
        </div>
      </li>
    `
  }

  private renderError (): string {
    return `<div class="associated-persons-state py-5 text-center text-danger">${trans(LOADING_FAILED, {}, 'associated-persons')}</div>`
  }

  private renderRecentDate (ap: ApResult): string {
    const updatedAt = ap.updated_at ?? ap.created_at

    if (typeof updatedAt !== 'string' || updatedAt === '') {
      return ''
    }

    const date = parseISO(updatedAt)

    if (Number.isNaN(date.getTime())) {
      return ''
    }

    const dateFormat = accountDateFormat.replace('mm', 'MM')
    const formatIn = (timezone: string, withTimezone: boolean): string => formatTz(
      utcToZonedTime(date, timezone),
      `${dateFormat} ${getTimestampFormat(accountIsUseAmPm, withTimezone)}`,
      { timeZone: timezone }
    )

    const timezonesDiffer = selectedTimezone !== accountTimezone &&
      formatIn(accountTimezone, false) !== formatIn(selectedTimezone, false)
    const secondaryHtml = timezonesDiffer
      ? `<small class="text-muted">${formatIn(selectedTimezone, true)}</small>`
      : ''

    return `
      <div class="badge badge-dot mr-4 btn-group-vertical" style="display: flex">
        <p class="name mb-0 item-text rtext-sm">${formatIn(accountTimezone, timezonesDiffer)}</p>
        ${secondaryHtml}
      </div>
    `
  }

  private getInitials (fullName: string): string {
    const initials = fullName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(namePart => namePart[0]?.toUpperCase() ?? '')
      .join('')

    return escapeHtml(initials || '?')
  }

  private avatarColours (fullName: string): { bg: string, fg: string } {
    const palette = [
      { bg: '#dce9fb', fg: '#2f6fd0' },
      { bg: '#f6e3cf', fg: '#9a5b1e' },
      { bg: '#e7e9ec', fg: '#52606d' },
      { bg: '#e3efe4', fg: '#3f7a4a' },
      { bg: '#f3e4ef', fg: '#8a3f7a' }
    ]

    let hash = 0
    for (let i = 0; i < fullName.length; i++) {
      hash = (hash + fullName.charCodeAt(i)) % palette.length
    }

    return palette[hash]
  }
}
